'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {AppIcon, FaPlayIcon} from '../icon';
import {MediaType, type MediaAsset} from './types';
import {VideoFirstFrame} from './VideoFirstFrame';

// Figma "Video Play Button" (1:10534): an 85px dark circle centered on the
// thumbnail (not a full-bleed scrim).
const PLAY_BUTTON_SIZE = 85;

const Tile = styled('button')(({theme}) => ({
  position: 'relative',
  display: 'block',
  // Width is supplied by the carousel Track's `& > *` rule (3-up on desktop);
  // a `width: 100%` here would override it and collapse the strip to one tile.
  aspectRatio: '16 / 9',
  padding: 0,
  border: 'none',
  borderRadius: theme.radii.images,
  overflow: 'hidden',
  cursor: 'pointer',
  background: theme.palette.colors.surface.neutral.subtle,
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.colors.border.focus.default}`,
    outlineOffset: 2,
  },
  '&:hover [data-tile-media]': {transform: 'scale(1.03)'},
}));

const Media = styled(Box, {shouldForwardProp: propName => propName !== '$src'})<{$src?: string}>(({$src}) => ({
  position: 'absolute',
  inset: 0,
  ...($src && {backgroundImage: `url(${$src})`, backgroundSize: 'cover', backgroundPosition: 'center'}),
}));

const PlayOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

const PlayButton = styled(Box)(({theme}) => ({
  width: PLAY_BUTTON_SIZE,
  height: PLAY_BUTTON_SIZE,
  borderRadius: theme.radii.full,
  background: theme.palette.colors.surface.darkOverlayStrong,
  color: theme.palette.colors.icon.neutral.inverse,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export interface GalleryTileProps {
  asset: MediaAsset;
  onOpen: () => void;
}

export function GalleryTile({asset, onOpen}: GalleryTileProps) {
  const isVideo = asset.type === MediaType.Video;
  const showFirstFrame = isVideo && !asset.poster;
  return (
    <Tile type="button" onClick={onOpen} aria-label={asset.alt}>
      {showFirstFrame ? (
        <VideoFirstFrame data-tile-media src={asset.src} />
      ) : (
        <Media data-tile-media $src={isVideo ? asset.poster : asset.src} />
      )}
      {isVideo && (
        <PlayOverlay>
          <PlayButton>
            <AppIcon icon={FaPlayIcon} aria-hidden sx={{fontSize: 40}} />
          </PlayButton>
        </PlayOverlay>
      )}
    </Tile>
  );
}

GalleryTile.displayName = 'GalleryTile';
